import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import Loading from "../../common/components/Loading";
import Box from "@mui/material/Box/Box";
import PeakFormContainer from "./PeakForm/PeakFormContainer";
import PeakFilters from "./Filters/PeakFilters";
import Grid from "@mui/material/Grid/Grid";
import DialogComponent from "../../common/components/DialogComponent";
import {useRecoilState} from "recoil";
import {initialPeakCreation, peakCreationAtom,} from "../../state/peaksRecoil";
import React, {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {usePeakRepository} from "../../api/repositories/PeakRepository";
import {usePeakDetailRepository} from "../../api/repositories/PeakDetailRepository";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../api/types/generic";
import {CommercialCategorySelectFE} from "../../api/types/commercialCategory";
import {fromSingleGenericBeToSingleGenericFe, useActionColDef,} from "../../utils";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {peakColDef} from "./peakColDef";
import ActionsPopover from "../../common/components/ActionsPopover";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import dayjs from "dayjs";
import BasicToolbar from "../../common/components/BasicToolbar";
import {FormSearchPeakModel, PeakEntityResponse,} from "../../api/types/peak";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {PeakDetail} from "../../api/types/peakDetail";

const Peaks = () => {
    // Use Recoil
    const [creationForm, setCreationForm] = useRecoilState(peakCreationAtom);

    // Use State
    const [, setPeakUsed] = useState<boolean>(false)

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGridLocale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {getPeakPaged, deletePeak, terminatePeak, getPeakInUse} =
        usePeakRepository();
    const {getPeakMatrix} = usePeakDetailRepository();

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [isCreationFormOpen, setIsCreationFormOpen] = useState(false);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchPeakModel>({
        name: "",
        commercialCategoryId: null,
        commercialSegmentId: null,
        from: "",
        to: "",
        showOnlyActive: false,
        dealers: dealerOption || [],
        legalEntities: legalEntityOptions
    })

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<PeakEntityResponse, FormSearchPeakModel>({
            gridRef,
            api: getPeakPaged,
            initialSortColId: "name",
            entityFilters: {
                name: "",
                commercialCategoryId: "",
                commercialSegmentId: "",
                from: "",
                to: "",
                showOnlyActive: false,
                dealers: dealerFilteredOptions,
                legalEntities: everyLegalEntitiesWithoutAll
            } as FormSearchPeakModel,
            customSort: "PeakEntityResponse",
        });

    useEffect(() => {
        if (gridRef?.current)
            handleTriggerFilters({
                name: testFilters.name,
                commercialSegmentId:
                    (
                        testFilters.commercialSegmentId as SelectElementModelFE
                    )?.code?.toString() || "",
                commercialCategoryId:
                    (
                        testFilters.commercialCategoryId as SelectElementModelFE
                    )?.code?.toString() || "",
                from: testFilters.from
                    ? dayjs(testFilters.from).toISOString()
                    : testFilters.from,
                to: testFilters.to
                    ? dayjs(testFilters.to).toISOString()
                    : testFilters.to,
                showOnlyActive: testFilters.showOnlyActive,
                legalEntities: Array.isArray(testFilters.legalEntities) 
                    ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
                dealers: Array.isArray(testFilters.dealers)
                    ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                    : [],
            });
    }, [testFilters.showOnlyActive]);

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef<PeakEntityResponse>[] = peakColDef({
        actionsRenderer: (params: ICellRendererParams) => (
            <ActionsPopover
                params={params}
                view={{
                    action: viewButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_VIEW_PEAK";
                        });
                    }),
                }}
                update={{
                    action: updateButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_UPDATE_PEAK";
                        });
                    }),
                }}
                del={{
                    action: deleteButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_DELETE_PEAK";
                        });
                    }),
                }}
                terminate={{
                    action: terminateButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_TERMINATE_PEAK";
                        });
                    }),
                }}
            />
        ),
        actionColDef,
        user
    });

    const defaultColDef = DEFAULT_COL_DEF;

    /* TABLE ACTIONS - START */
    const viewButtonClicked = async (data: PeakEntityResponse) => {
        const matrixData = await getPeakMatrix(data.id);
        setCreationForm({
            readOnly: true,
            inUse: false,
            peakId: data.id,
            definitionData: getDefinitionData(data),
            matrixData: convertDaysLanguage(matrixData),
            step: 0,
            completed: [true, true, true]
        });
        setIsCreationFormOpen(true);
    };

    const getDefinitionData = (data: PeakEntityResponse) => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegmentId: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const i18nDays: { [key: string]: number } = {
        "MONDAY": 1, "TUESDAY": 2, "WEDNESDAY": 3, "THURSDAY": 4, "FRIDAY": 5, "SATURDAY": 6, "SUNDAY": 0,
    }
    const updateButtonClicked = async (data: PeakEntityResponse) => {
        const definitionData = getDefinitionData(data);
        const matrixData = await getPeakMatrix(data.id);
        const peakInUse: boolean = await getPeakInUse({peakId: data.id});
        setPeakUsed(peakInUse)
        setCreationForm({
            readOnly: false,
            inUse: peakInUse,
            peakId: data.id,
            newId: data.id,
            definitionData,
            matrixData: convertDaysLanguage(matrixData),
            step: 0,
            completed: [
                !!definitionData,
                Boolean(matrixData?.length),
                Boolean(matrixData?.length)
            ]
        });
        setIsCreationFormOpen(true);
    };

    const convertDaysLanguage = (matrixData: PeakDetail[]) => {
        return matrixData?.map(p => {
            return {
                ...p,
                peakDay: p.peakDay !== null && p.peakDay !== 'undefined' ? dayjs().day(i18nDays[p.peakDay as string] || 0).format("dddd").toUpperCase() : t('pricing:days.none'),
                peakOffDay: p.peakOffDay !== null && p.peakOffDay !== 'undefined' ? dayjs().day(i18nDays[p.peakOffDay as string] || 0).format("dddd").toUpperCase() : t('pricing:days.none'),
            }
        });
    }

    const terminateButtonClicked = async (data: PeakEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmTerminate(data.id),
            message: t("pricing:peaks.dialogContents.terminatePeakInfo"),
        });
    };

    const handleConfirmTerminate = async (id: number) => {
        await terminatePeak(id);

        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const deleteButtonClicked = (data: PeakEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:peaks.dialogContents.removePeakInfo"),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deletePeak(id);
                setDialog(null);
                refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };
    /* TABLE ACTIONS - END */

    const handleTriggerResetFilters = () => {
        setTestFilters({
            name: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            from: "",
            to: "",
            showOnlyActive: false,
            dealers: dealerOption || [], // Imposta oggetti nello state
            legalEntities: legalEntityOptions // Imposta oggetti nello state
        });
        handleTriggerFilters({
            name: "",
            commercialCategoryId: "",
            commercialSegmentId: "",
            from: "",
            to: "",
            showOnlyActive: false,
            dealers: dealerFilteredOptions, // Passa stringhe all'API
            legalEntities: legalEntityOptions?.map((le: SelectElementModelFE) => le.code as string) || [] // Passa stringhe all'API
        });
    };

    const handleGetPeakList = () => {
        handleTriggerFilters({
            name: testFilters.name,
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            from: testFilters.from
                ? dayjs(testFilters.from).toISOString()
                : testFilters.from,
            to: testFilters.to
                ? dayjs(testFilters.to).toISOString()
                : testFilters.to,
            showOnlyActive: testFilters.showOnlyActive,
            legalEntities: Array.isArray(testFilters.legalEntities) 
                ? (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)
                : [],
            dealers: Array.isArray(testFilters.dealers)
                ? (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)
                : [],
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleGetPeakList();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const onDone = () => {
        setIsCreationFormOpen(false);
        setCreationForm(initialPeakCreation);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const onClose = () => {
        creationForm.readOnly || refresh();
        setIsCreationFormOpen(false);
        setCreationForm(initialPeakCreation);
    };

    const createButtonClicked = async () => {
        setCreationForm({
            readOnly: false,
            inUse: false,
            definitionData: undefined,
            step: 0,
            completed: [false, false, false],
        });
        setIsCreationFormOpen(true);
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_PEAK";
                        });
                    })
                        ? {onClick: createButtonClicked}
                        : undefined
                }
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => {
                        setTestFilters({
                            ...testFilters,
                            showOnlyActive: checked,
                        });
                    },
                    value: testFilters.showOnlyActive as boolean,
                }}
            />
        ),
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_PEAK.name")}/>

            <PeakFormContainer
                onDone={onDone}
                isOpen={isCreationFormOpen}
                onClose={onClose}
                refresh={refresh}
            />

            <PeakFilters
                handleGetPeakList={handleGetPeakList}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {dialog?.open && (
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    )}
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default Peaks;
